import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import OtpInput from 'react-otp-input';
import logo from '../../Assets/Logo/logo.png';
import DefaultSEO from '../../components/DefaultSEO';

const OtpPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const phone = location.state?.phone || '+91 9876543210'; // Fallback for direct navigation
    const [otp, setOtp] = useState('');
    const [seconds, setSeconds] = useState(60);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Redirect already logged-in users away from OtpPage
    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            navigate('/', { replace: true });
        }
    }, [navigate]);

    useEffect(() => {
        if (seconds > 0) {
            const timer = setTimeout(() => setSeconds((s) => s - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [seconds]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (otp.length === 4) {
            setLoading(true);
            try {
                const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
                const response = await fetch(`${apiBase}/auth/verify-otp`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify({ phone, otp })
                });

                const data = await response.json();

                if (data.success && data.data?.token) {
                    localStorage.setItem('auth_token', data.data.token);
                    
                    const isNewUser = data.data.is_new_user;
                    const from = location.state?.from;
                    
                    if (isNewUser) {
                        navigate('/complete-profile', { replace: true, state: { from } });
                    } else {
                        if (from) {
                            navigate(from.pathname || from, { replace: true, state: from.state });
                        } else {
                            navigate('/', { replace: true });
                        }
                    }
                } else {
                    throw new Error(data.message || "Invalid OTP code");
                }
            } catch (err) {
                console.error(err);
                setError(err.message || 'Invalid OTP code. Please try again.');
            } finally {
                setLoading(false);
            }
        } else {
            setError('Please enter a 4-digit OTP.');
        }
    };

    const handleResend = async () => {
        try {
            setSeconds(60);
            setError('');
            const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
            const response = await fetch(`${apiBase}/auth/send-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ phone })
            });

            const data = await response.json();
            if (!data.success) {
                throw new Error(data.message || 'Failed to resend OTP');
            }
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to resend OTP. Please try again.');
            setSeconds(0);
        }
    };

    return (
        <div className="d-flex align-items-center justify-content-center min-vh-100">
            <DefaultSEO title="Verify OTP | ZoyoTrip" />
            {/* Cross icon for mobile */}
            <button
                className="btn position-absolute top-0 start-0 m-3 d-lg-none"
                onClick={() => navigate(-1)}
                aria-label="Close"
            >
                <i className="bi bi-x-lg fs-4 text-dark"></i>
            </button>

            <div className="p-4 w-100" style={{ maxWidth: '420px' }}>
                {/* Logo and Title */}
                <div className="d-flex flex-column align-items-center mb-4">
                    <Link to={'/'}>
                        <img src={logo} alt="ZoyoTrip" width="100" className="mb-2" />
                    </Link>
                    <h5 className="fw-bold mt-2">Verify Your Account</h5>
                    <p className="small text-muted mb-1">Enter the 4-digit code sent to</p>
                    <p className="small text-muted">
                        <span className="fw-semibold text-dark">{phone}</span>{' '}
                        <button
                            className="btn btn-sm btn-link text-primary text-decoration-none p-0 ms-2"
                            onClick={() => navigate('/login')}
                        >
                            <i className="bi bi-pencil-square me-1"></i>Edit
                        </button>
                    </p>
                </div>

                {error && <div className="alert alert-danger small py-2 text-center">{error}</div>}

                {/* OTP Input */}
                <form onSubmit={handleSubmit}>
                    <div className="d-flex justify-content-center mb-3">
                        <OtpInput
                            value={otp}
                            onChange={setOtp}
                            numInputs={4}
                            shouldAutoFocus
                            renderSeparator={<span> </span>}
                            renderInput={(props) => <input {...props} />}
                            inputStyle={{
                                width: '3rem',
                                height: '3rem',
                                margin: '0 0.4rem',
                                fontSize: '1.5rem',
                                borderRadius: '0.5rem',
                                border: '1px solid #ced4da',
                                textAlign: 'center',
                                backgroundColor: '#f8f9fa',
                            }}
                            focusStyle={{
                                border: '1px solid #007bff',
                                outline: 'none',
                            }}
                        />
                    </div>

                    {/* Timer or Resend */}
                    <div className="text-center mb-4">
                        {seconds > 0 ? (
                            <small className="text-muted">Resend OTP in <strong>{seconds}s</strong></small>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-link text-primary p-0 small text-decoration-none"
                                onClick={handleResend}
                            >
                                Resend OTP
                            </button>
                        )}
                    </div>

                    {/* Submit Button */}
                    <div className="d-grid">
                        <button type="submit" className="btn btn-primary rounded-pill" disabled={loading}>
                            {loading ? 'Verifying...' : 'Verify & Continue'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OtpPage;
